import { Directive, Renderer2, OnDestroy, ElementRef, AfterViewInit } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/core';

/**
 * Enhances a multiple mat-select panel with:
 *   - a type-to-filter search box pinned to the TOP of the panel
 *   - a "Select All/None" button pinned to the BOTTOM of the panel
 *
 * Both are injected as `position: sticky` children of `.mat-select-panel` (the
 * element that actually scrolls), so they stay put while the option list moves
 * under them. They are deliberately NOT siblings of the panel: mat-select
 * computes the overlay's vertical offset from the selected option's position,
 * and growing the panel's wrapper throws that math off on all ~136 selects
 * using this directive.
 *
 * WHY A MUTATION OBSERVER AND NOT openedChange. Material emits `openedChange` (and the
 * `opened`/`closed` streams derived from it) out of the @transformPanel animation's `done`
 * callback. That callback does not run in this build — Angular 15 core against Material
 * 14.2.7 — so the button silently stopped being injected on all selects using this
 * directive: no error, panel and parentNode both perfectly valid, the subscription just
 * never fired. `panelOpen` is set synchronously by open(), and the host mirrors it in
 * aria-expanded, so watching that attribute is both animation-independent and public API.
 * It also catches every way a panel opens — mouse, keyboard and programmatic — which a
 * click handler would not.
 */
@Directive({
	selector: '[appSelectAll]',
})
export class MultiselectDirective implements OnDestroy, AfterViewInit {
	private observer: MutationObserver | null = null;
	/** Teardown for listeners bound to the current panel; the panel is rebuilt per open. */
	private panelTeardown: Array<() => void> = [];
	private searchInput: HTMLInputElement | null = null;
	private countEl: HTMLElement | null = null;
	private toggleLabelEl: HTMLElement | null = null;
	private query = '';

	constructor(private el: ElementRef, private select: MatSelect, private renderer: Renderer2) { }

	ngAfterViewInit(): void {
		// The panel lives in a CDK overlay that is created on open and destroyed on
		// close, so the chrome has to be injected per open rather than once.
		this.observer = new MutationObserver(() => {
			if (this.select.panelOpen) {
				this.injectChrome();
			} else {
				this.teardownPanel();
			}
		});
		this.observer.observe(this.el.nativeElement, { attributes: true, attributeFilter: ['aria-expanded'] });

		const parent = this.el.nativeElement.parentElement;
		if (parent) {
			parent.removeAttribute('aria-owns');
			parent.removeAttribute('aria-labelledby');
		}
	}

	// ── Injection ────────────────────────────────────────────────────────────

	private injectChrome(): void {
		const panel = this.select.panel;
		const panelEl: HTMLElement | null = panel && panel.nativeElement ? panel.nativeElement : null;
		if (!panelEl) {
			return;
		}
		// aria-expanded can be rewritten while the same panel is up; never stack copies.
		if (panelEl.querySelector('.select-all-container')) {
			return;
		}

		this.renderer.addClass(panelEl, 'msel-panel');

		// Every open starts unfiltered — a stale query from the last open would
		// silently hide options the user never filtered out.
		this.query = '';
		this.showAllOptions();

		this.buildSearch(panelEl);
		this.buildFooter(panelEl);
		this.updateCount();

		// Any click on an option changes the count; recompute after Material has
		// applied the new value.
		this.panelTeardown.push(
			this.renderer.listen(panelEl, 'click', () => { setTimeout(() => this.updateCount()); })
		);
	}

	private buildSearch(panelEl: HTMLElement): void {
		const row = this.renderer.createElement('div');
		this.renderer.addClass(row, 'msel-search');
		// An <input> is not valid inside role="listbox"; keep it out of the a11y tree
		// as a row so screen readers still walk the options cleanly.
		this.renderer.setAttribute(row, 'role', 'presentation');

		const icon = this.renderer.createElement('i');
		this.renderer.addClass(icon, 'material-icons');
		this.renderer.addClass(icon, 'msel-search-icon');
		this.renderer.appendChild(icon, this.renderer.createText('search'));
		this.renderer.appendChild(row, icon);

		const input: HTMLInputElement = this.renderer.createElement('input');
		this.renderer.addClass(input, 'msel-search-input');
		this.renderer.setAttribute(input, 'type', 'text');
		this.renderer.setAttribute(input, 'placeholder', 'Filter options…');
		this.renderer.setAttribute(input, 'autocomplete', 'off');
		this.renderer.setAttribute(input, 'aria-label', 'Filter options');
		this.renderer.appendChild(row, input);
		this.searchInput = input;

		const clear = this.renderer.createElement('button');
		this.renderer.addClass(clear, 'msel-search-clear');
		this.renderer.setAttribute(clear, 'type', 'button');
		this.renderer.setAttribute(clear, 'aria-label', 'Clear filter');
		this.renderer.setAttribute(clear, 'tabindex', '-1');
		const clearIcon = this.renderer.createElement('i');
		this.renderer.addClass(clearIcon, 'material-icons');
		this.renderer.appendChild(clearIcon, this.renderer.createText('close'));
		this.renderer.appendChild(clear, clearIcon);
		this.renderer.appendChild(row, clear);

		this.panelTeardown.push(
			this.renderer.listen(input, 'input', () => this.applyFilter(input.value)),
			// The overlay is not a DOM descendant of the mat-select host, so Material
			// never sees keys typed in here — hence the local Escape handling. Note
			// we do NOT call select.close(): in this Angular 15 / Material 14.2.7
			// pairing close() flips panelOpen but the @transformPanel done callback
			// that detaches the overlay never fires (same root cause as the
			// MutationObserver note above), so the panel would stay on screen.
			// Handing focus back to the trigger lets Material's own tested keyboard
			// path own closing instead.
			this.renderer.listen(input, 'keydown', (e: KeyboardEvent) => {
				e.stopPropagation();
				if (e.key === 'Escape') {
					e.preventDefault();
					if (this.query) {
						input.value = '';
						this.applyFilter('');
					} else {
						try { this.el.nativeElement.focus(); } catch { /* trigger gone */ }
					}
				}
				if (e.key === 'Enter') { e.preventDefault(); }
			}),
			// Clicks in the chrome must never reach the option list.
			this.renderer.listen(row, 'click', (e: Event) => { e.stopPropagation(); }),
			this.renderer.listen(row, 'mousedown', (e: Event) => { e.stopPropagation(); }),
			this.renderer.listen(clear, 'click', (e: Event) => {
				e.stopPropagation();
				e.preventDefault();
				input.value = '';
				this.applyFilter('');
				input.focus();
			})
		);

		this.renderer.insertBefore(panelEl, row, panelEl.firstChild);

		// Opening the panel to type is the common case; put the caret in the box.
		setTimeout(() => { try { input.focus(); } catch { /* panel already gone */ } });
	}

	private buildFooter(panelEl: HTMLElement): void {
		const div = this.renderer.createElement('div');
		this.renderer.addClass(div, 'select-all-container');
		this.renderer.setAttribute(div, 'role', 'presentation');

		const button = this.renderer.createElement('button');
		this.renderer.addClass(button, 'select-all-button');
		// type=button: without it this submits any form the select sits inside.
		this.renderer.setAttribute(button, 'type', 'button');
		const label = this.renderer.createElement('span');
		this.renderer.appendChild(label, this.renderer.createText('Select All/None'));
		this.renderer.appendChild(button, label);
		this.toggleLabelEl = label;

		const count = this.renderer.createElement('span');
		this.renderer.addClass(count, 'msel-count');
		this.countEl = count;

		this.renderer.appendChild(div, button);
		this.renderer.appendChild(div, count);
		this.renderer.appendChild(panelEl, div);

		this.panelTeardown.push(
			this.renderer.listen(button, 'click', (event: Event) => {
				// The panel closes on any click that reaches it; keep it open so the user can
				// see what the toggle did and carry on adjusting.
				event.stopPropagation();
				event.preventDefault();
				this.selectAllToggle();
			}),
			this.renderer.listen(div, 'mousedown', (e: Event) => { e.stopPropagation(); })
		);
	}

	// ── Filtering ────────────────────────────────────────────────────────────

	private applyFilter(value: string): void {
		this.query = (value || '').trim().toLowerCase();

		this.allOptions().forEach(option => {
			const host = option._getHostElement();
			if (!host) { return; }
			const hit = !this.query || this.optionLabel(option).indexOf(this.query) > -1;
			this.renderer.setStyle(host, 'display', hit ? '' : 'none');
		});

		this.updateCount();
	}

	private showAllOptions(): void {
		this.allOptions().forEach(option => {
			const host = option._getHostElement();
			if (host) { this.renderer.setStyle(host, 'display', ''); }
		});
	}

	private allOptions(): MatOption[] {
		return this.select.options ? this.select.options.toArray() : [];
	}

	/** Options currently passing the filter — the scope Select All/None acts on. */
	private visibleOptions(): MatOption[] {
		if (!this.query) { return this.allOptions(); }

		return this.allOptions().filter(o => this.optionLabel(o).indexOf(this.query) > -1);
	}

	private optionLabel(option: MatOption): string {
		const raw = typeof option.getLabel === 'function' ? option.getLabel() : option.viewValue;

		return (raw || '').toLowerCase();
	}

	// ── Selection ────────────────────────────────────────────────────────────

	/** Honour the select's own compareWith so object-valued options match correctly. */
	private same(a: any, b: any): boolean {
		const cw = this.select.compareWith;

		return typeof cw === 'function' ? cw(a, b) : a === b;
	}

	/**
	 * Toggle every option currently in scope. With a filter active the scope is
	 * just the matches, and selections outside it are left alone — "filter to
	 * COLOR, select all" should add the colour lines, not throw away the rest.
	 * With no filter this reduces to the original whole-list select all/none.
	 */
	selectAllToggle(): void {
		// An untouched multiple select holds null, not [] — .length on it threw.
		const current: any[] = Array.isArray(this.select.value) ? this.select.value : [];
		const scope = this.visibleOptions().map(o => o.value);
		if (!scope.length) { return; }

		const allSelected = scope.every(v => current.some(c => this.same(c, v)));

		let next: any[];
		if (allSelected) {
			next = current.filter(c => !scope.some(v => this.same(c, v)));
		} else {
			next = current.slice();
			scope.forEach(v => {
				if (!next.some(c => this.same(c, v))) { next.push(v); }
			});
		}

		// Assigning .value runs Material's ControlValueAccessor onChange, so a bound
		// FormControl sees this and emits valueChanges. Consumers that need to react
		// must listen on valueChanges — this path does NOT emit selectionChange.
		this.select.value = next;
		this.updateCount();
	}

	private updateCount(): void {
		const current: any[] = Array.isArray(this.select.value) ? this.select.value : [];
		const scope = this.visibleOptions().map(o => o.value);
		const inScope = scope.filter(v => current.some(c => this.same(c, v))).length;

		if (this.countEl) {
			// The panel is capped at 280px wide and the button takes most of it, so
			// keep this terse or it just ellipsises away.
			this.countEl.textContent = this.query
				? `${inScope}/${scope.length} · ${current.length} sel`
				: `${current.length} selected`;
		}
		if (this.toggleLabelEl) {
			const allSelected = scope.length > 0 && inScope === scope.length;
			this.toggleLabelEl.textContent = this.query
				? (allSelected ? 'Deselect matches' : 'Select matches')
				: 'Select All/None';
		}
	}

	// ── Teardown ─────────────────────────────────────────────────────────────

	private teardownPanel(): void {
		this.panelTeardown.forEach(off => {
			try { off(); } catch { /* listener host already destroyed */ }
		});
		this.panelTeardown = [];
		// The panel DOM is gone, but the MatOption instances survive — leaving them
		// display:none would hide options in the next panel that opens.
		this.query = '';
		this.showAllOptions();
		this.searchInput = null;
		this.countEl = null;
		this.toggleLabelEl = null;
	}

	ngOnDestroy(): void {
		this.teardownPanel();
		if (this.observer) {
			this.observer.disconnect();
			this.observer = null;
		}
	}
}
